<script setup lang="ts">
import { ChevronLeftIcon, ChevronRightIcon } from '@blpde/nx-icons';
import {
  ComponentPublicInstance,
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  useTemplateRef,
} from 'vue';
import { NxButton } from '../Button';
import { NxUtilityButton } from '../UtilityButton';
import { UtilityPanelProps } from './internal';
import { throttle } from '../lib/throttle';
import { NxTooltip } from '../Tooltip';

const props = withDefaults(defineProps<UtilityPanelProps>(), {
  utilityItems: undefined,
  maxPanelCount: 6,
  enableScrollArrows: true,
});

const scrollContainerRef = useTemplateRef('scrollContainerRef');
const itemRefs = ref<Map<number, HTMLElement>>(new Map());
const showLeftArrow = ref(false);
const showRightArrow = ref(false);
let resizeObserver: ResizeObserver | null = null;

const utilityItems = computed(() => {
  if (
    !props.enableScrollArrows &&
    Array.isArray(props.utilityItems) &&
    props.utilityItems?.length > props.maxPanelCount
  ) {
    return props.utilityItems?.slice(0, props.maxPanelCount);
  }

  return props.utilityItems ?? [];
});

/**
 * Stores a reference to a utility item element in the itemRefs map.
 */
const setItemRef = (el: Element | ComponentPublicInstance | null, index: number) => {
  if (!el) {
    return;
  }

  const domNode = '$el' in el ? (el.$el as HTMLElement) : (el as HTMLElement);
  itemRefs.value.set(index, domNode);
};

/**
 * Updates the visibility of left/right scroll arrows based on the current
 * scroll position of the utility panel container.
 */
const checkScroll = () => {
  const el = scrollContainerRef.value;

  if (!el) {
    return;
  }

  // Show left arrow if scroll to the right has already happened
  showLeftArrow.value = el.scrollLeft > 0;
  // Show right arrow if already scrolled area (scrollLeft) and the visible area (clientWidth) are smaller than the whole available scrollWidth
  showRightArrow.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
};

/**
 * Throttle checkScroll to prevent flooding with calls for example on resize
 */
const throttledCheckScroll = throttle(checkScroll, 100);

/**
 * Scrolls the utility panel container in the specified direction.
 */
const scroll = (direction: 'left' | 'right') => {
  const container = scrollContainerRef.value;
  if (!container) {
    return;
  }

  // Get current scroll position
  const currentScroll = container.scrollLeft;
  const itemCount = utilityItems.value.length;
  const buffer = 5;

  let scrollToTarget = currentScroll;

  if (direction === 'right') {
    // Iterate from start to end
    for (let i = 0; i < itemCount; i++) {
      const el = itemRefs.value.get(i);
      if (!el) {
        continue;
      }

      // Find first element whose left border is beyond the visible right edge
      if (el.offsetLeft > currentScroll + buffer) {
        scrollToTarget = el.offsetLeft;
        break;
      }
    }

    // No element was found. Scroll to the very right.
    if (scrollToTarget === currentScroll) {
      scrollToTarget = container.scrollWidth;
    }
  } else {
    // Iterate from end to start
    for (let i = itemCount - 1; i >= 0; i--) {
      const el = itemRefs.value.get(i);
      if (!el) {
        continue;
      }

      // Find first element whose left border is before the visible left edge
      if (el.offsetLeft < currentScroll - buffer) {
        scrollToTarget = el.offsetLeft;
        break;
      }
    }

    // No element was found. Scroll to the very left.
    if (scrollToTarget === currentScroll) {
      scrollToTarget = 0;
    }
  }

  // Scroll to set target
  container.scrollTo({
    left: scrollToTarget,
    behavior: 'smooth',
  });
};

/**
 * Initialize ResizeObserver and execute checkScroll. Called onMounted.
 */
function initialScrollCheck() {
  if (!props.enableScrollArrows) {
    return;
  }

  const el = scrollContainerRef.value;

  if (el && !resizeObserver) {
    el.addEventListener('scroll', throttledCheckScroll);

    resizeObserver = new ResizeObserver(() => {
      throttledCheckScroll();
    });
    resizeObserver.observe(el);

    checkScroll();
  }
}

/**
 * Unsubscribes from the ResizeObserver and removes scroll event listener. Called onBeforeUnmount.
 */
function unmountResizeObserver() {
  if (!props.enableScrollArrows) {
    return;
  }

  const el = scrollContainerRef.value;

  if (el) {
    el.removeEventListener('scroll', throttledCheckScroll);
  }

  if (resizeObserver) {
    resizeObserver.disconnect();
  }
}

onMounted(() => {
  initialScrollCheck();
});

onBeforeUnmount(() => {
  unmountResizeObserver();
});
</script>

<template>
  <div class="panel-wrapper">
    <div v-if="utilityItems.length > 0" class="scroll-icon-container">
      <NxButton
        v-if="showLeftArrow"
        class="scroll-arrow left"
        variant="outline"
        width="icon-only"
        @click="scroll('left')"
      >
        <ChevronLeftIcon :filled="true" class="nxt-icon-lg" />
      </NxButton>

      <div ref="scrollContainerRef" class="panel-icon-container">
        <div v-for="(utilityItem, index) in utilityItems" :key="index" class="panel-icon-wrapper">
          <NxTooltip
            v-if="utilityItem.tooltip?.text"
            class="icon-tooltip"
            :text="utilityItem.tooltip?.text"
            :side="utilityItem.tooltip?.side"
          >
            <NxButton
              :ref="(el) => setItemRef(el, index)"
              class="panel-icon"
              variant="outline"
              width="icon-only"
              @click="utilityItem.onClick()"
            >
              <component
                :is="utilityItem.icon.component"
                v-if="utilityItem.icon"
                :filled="utilityItem.icon.filled ?? true"
                :color="utilityItem.icon.color"
                :size="utilityItem.icon.size"
                :class="utilityItem.icon.size ? undefined : 'nxt-icon-lg'"
              />
            </NxButton>
          </NxTooltip>
          <NxButton
            v-else
            :ref="(el) => setItemRef(el, index)"
            class="panel-icon"
            variant="outline"
            width="icon-only"
            @click="utilityItem.onClick()"
          >
            <component
              :is="utilityItem.icon.component"
              v-if="utilityItem.icon"
              :filled="utilityItem.icon.filled ?? true"
              :color="utilityItem.icon.color"
              :size="utilityItem.icon.size"
              :class="utilityItem.icon.size ? undefined : 'nxt-icon-lg'"
            />
          </NxButton>
        </div>
      </div>

      <NxButton
        v-if="showRightArrow"
        class="scroll-arrow right"
        variant="outline"
        width="icon-only"
        @click="scroll('right')"
      >
        <ChevronRightIcon :filled="true" class="nxt-icon-lg" />
      </NxButton>
    </div>

    <NxUtilityButton
      v-if="props.dropdownElements.length > 0"
      :flip-dropdown="props.flipDropdown"
      :dropdown-elements="props.dropdownElements"
      :label="props.label"
    >
      <slot></slot>
    </NxUtilityButton>
  </div>
</template>

<style lang="css" scoped>
.panel-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.scroll-icon-container {
  --border-color: var(--nxt-border);
  display: flex;
  align-items: center;
  position: relative;
  margin-bottom: var(--nxt-space-xs);
  min-width: 0;

  .scroll-arrow {
    flex-shrink: 0;
    z-index: var(--nxt-z-above);
    border: calc(var(--nxt-hairline) * 2) solid var(--border-color);
    height: 2.8125em;
    border-radius: var(--nxt-radius-sm);
  }

  .scroll-arrow.left {
    margin-right: var(--nxt-space-2xs);
  }

  .scroll-arrow.right {
    margin-left: var(--nxt-space-2xs);
  }
}

.panel-icon-container {
  display: flex;
  flex-direction: row;
  column-gap: var(--nxt-space-xs);
  overflow-x: auto;
  scroll-behavior: smooth;
  white-space: nowrap;
  scrollbar-width: none;
  flex: 1;
}

:deep(.icon-tooltip) {
  display: flex;
  flex: 1;

  .panel-icon {
    flex: 1 0 auto;
    box-sizing: border-box;
    height: 2.8125em;
    border: var(--nxt-hairline) solid var(--border-color);
    border-radius: var(--nxt-radius-sm);
    box-shadow: var(--nxt-shadow-sm);
  }
}

.panel-icon-wrapper {
  flex-direction: row;
  display: flex;
  column-gap: var(--nxt-space-xs);
  flex: 1;

  .panel-icon {
    flex: 1 0 auto;
    box-sizing: border-box;
    height: 2.8125em;
    border: var(--nxt-hairline) solid var(--border-color);
    border-radius: var(--nxt-radius-sm);
    box-shadow: var(--nxt-shadow-sm);
  }
}
</style>
